/**
 * useChampionBadgeInjector Hook
 * 
 * Runtime DOM injection of "Champion Status" badge into dashboard/header.
 * Subtle but visible confirmation that user is opted-in to the championship.
 * Uses vanilla DOM manipulation to avoid React reconciliation.
 */

import { useEffect } from 'react';
import { trpc } from '@/lib/trpc';

export function useChampionBadgeInjector(): void {
  const meQuery = trpc.auth.me.useQuery();

  useEffect(() => {
    // Only inject if user is opted-in
    if (!meQuery.data?.raffleOptIn) {
      // Remove badge if it exists and user is not opted-in
      const existingBadge = document.getElementById('champion-status-badge');
      if (existingBadge) {
        existingBadge.remove();
      }
      return;
    }

    const injectBadge = () => {
      // Check if badge already exists
      if (document.getElementById('champion-status-badge')) {
        return;
      }

      // Find header or dashboard area to inject badge
      const header = document.querySelector('header') || document.querySelector('[role="banner"]');
      if (!header) {
        // Fallback: inject at top of body
        const badge = createBadgeElement();
        document.body.prepend(badge);
        console.log('[ChampionBadge] Badge injected at body top');
        return;
      }

      const badge = createBadgeElement();
      header.appendChild(badge);
      console.log('[ChampionBadge] Badge injected into header');
    };

    injectBadge();
  }, [meQuery.data?.raffleOptIn]);
}

function createBadgeElement(): HTMLDivElement {
  const badge = document.createElement('div');
  badge.id = 'champion-status-badge';
  badge.style.cssText = `
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 40;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    border-radius: 20px;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    font-family: system-ui, -apple-system, Roboto, Arial, sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #78350f;
    animation: slideIn 0.3s ease-out;
  `;

  const icon = document.createElement('span');
  icon.textContent = '🏆';
  icon.style.fontSize = '14px';

  const text = document.createElement('span');
  text.textContent = 'Champion';

  badge.appendChild(icon);
  badge.appendChild(text);

  // Add CSS animation
  if (!document.getElementById('champion-badge-styles')) {
    const style = document.createElement('style');
    style.id = 'champion-badge-styles';
    style.textContent = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }

  return badge;
}
